import React, { useState, useEffect } from 'react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import useAuth from '../../hooks/useAuth';
import paymentService from '../../services/paymentService';
import { checkAICredits } from '../../utils/aiService';
import { CreditCard, Calendar, DollarSign, AlertCircle, ExternalLink, Zap, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SubscriptionManagement = () => {
  const { subscription, getCurrentPlan, loading, refetchSubscription, refetchUserData, cancelSubscription } = useSubscription();
  const { user, userProfile, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState(false);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aiCredits, setAiCredits] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelConfirmLoading, setCancelConfirmLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [cancelSuccessDate, setCancelSuccessDate] = useState(null);

  const currentPlan = getCurrentPlan();

  // Fetch AI credits on component mount or when user changes
  useEffect(() => {
    const fetchAICredits = async () => {
      if (!user?.id) return;

      setCreditsLoading(true);
      try {
        const creditsInfo = await checkAICredits(user.id);
        if (creditsInfo.success) {
          setAiCredits(creditsInfo);
        }
      } catch (err) {
        console.error('Error fetching AI credits:', err);
        setAiCredits(null);
      } finally {
        setCreditsLoading(false);
      }
    };

    fetchAICredits();
  }, [user?.id, refreshTrigger]);

  // Refresh subscription data when component mounts
  useEffect(() => {
    const refreshSubscriptionData = async () => {
      if (!user?.id) return;
      
      try {
        // Refresh subscription data only, avoid refreshing user profile which can cause parent re-renders
        await Promise.all([
          refetchSubscription(),
          refetchUserData()
        ]);
      } catch (err) {
        console.error('Error refreshing subscription data:', err);
      }
    };

    refreshSubscriptionData();
  }, [user?.id]);

  // Check if we just returned from payment success
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      console.log('Payment success detected, session_id:', sessionId);
      let pollCount = 0;
      const maxPolls = 10;
      
      const refreshAfterPayment = async () => {
        try {
          console.log(`Polling for subscription update (attempt ${pollCount + 1}/${maxPolls})...`);
          
          // Wait before checking
          const waitTime = pollCount === 0 ? 3000 : 2000;
          await new Promise(resolve => setTimeout(resolve, waitTime));
          
          // Refresh all data
          await Promise.all([
            refetchSubscription(),
            refetchUserData(),
            refreshUserProfile()
          ]);
          
          // Trigger AI credits refresh
          setRefreshTrigger(prev => prev + 1);
          
          pollCount++;
          
          // Continue polling if not at max attempts
          if (pollCount < maxPolls) {
            console.log('Continuing to poll for updates...');
            setTimeout(refreshAfterPayment, 2000);
          } else {
            console.log('Polling complete. Subscription should be updated.');
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (err) {
          console.error('Error refreshing after payment:', err);
          pollCount++;
          
          if (pollCount < maxPolls) {
            setTimeout(refreshAfterPayment, 2000);
          } else {
            setError('Payment processed successfully! If your subscription hasn\'t updated, please use the Refresh button.');
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }
      };
      
      refreshAfterPayment();
    }
  }, []);

  const handleManageSubscription = async () => {
    if (!user) return;

    setActionLoading(true);
    setError(null);

    try {
      await paymentService.redirectToPortal(user.id);
    } catch (err) {
      console.error('Error opening customer portal:', err);
      setError('Failed to open subscription management. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!user?.id) {
      setError('User not authenticated');
      return;
    }

    setCancelConfirmLoading(true);
    setError(null);

    try {
      const result = await cancelSubscription();
      
      setShowCancelConfirm(false);

      // Show success modal with access period info
      if (result?.accessUntil) {
        const accessDate = new Date(result.accessUntil).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
        setCancelSuccessDate(accessDate);
      } else {
        setCancelSuccessDate(null);
      }
      setShowSuccessModal(true);
      
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setError(
        err.message || 'Failed to cancel subscription. Please try again or contact support.'
      );
    } finally {
      setCancelConfirmLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    
    // Check for invalid dates (including January 1, 1970 which indicates epoch/null)
    if (isNaN(date.getTime()) || date.getFullYear() === 1970) {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  const isFreeTier = !subscription || currentPlan?.tier === 'free';
  const isCancelled = subscription?.status === 'canceled';
  const isExpired = subscription?.status === 'expired';
  // "Pending cancellation" = cancelled in DB but access period hasn't ended yet
  const isPendingCancellation = isCancelled &&
    subscription?.current_period_end &&
    new Date(subscription.current_period_end) > new Date();

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <CreditCard className="mr-2 h-5 w-5" />
          Subscription Management
        </h3>
        <button
          onClick={async () => {
            setActionLoading(true);
            try {
              await Promise.all([
                refetchSubscription(),
                refetchUserData(),
                refreshUserProfile()
              ]);
              setRefreshTrigger(prev => prev + 1);
            } catch (err) {
              console.error('Error refreshing:', err);
            } finally {
              setActionLoading(false);
            }
          }}
          disabled={actionLoading}
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50"
        >
          <svg className={`h-4 w-4 ${actionLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      <div className="p-6">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2 mt-0.5" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        <div className="space-y-6">

          {/* Cancellation Notice */}
          {(isCancelled || isExpired) && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-orange-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-orange-800">
                    {isExpired ? 'Subscription Expired' : 'Subscription Cancelled'}
                  </p>
                  <p className="text-sm text-orange-700 mt-1">
                    {isExpired ? (
                      <>Your cancelled subscription period has ended. You are now on the Free Tier.</>
                    ) : (
                      <>
                        Your subscription has been cancelled. You will retain access to premium features until{' '}
                        <span className="font-semibold">
                          {formatDate(subscription?.current_period_end) !== 'N/A' 
                            ? formatDate(subscription?.current_period_end)
                            : 'the end of your billing period'}
                        </span>
                        . After that, you will be automatically downgraded to the Free Tier. No further charges will occur. You can resubscribe at any time to regain access to premium features.
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Current Plan */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Current Plan</h4>
            <div className={`rounded-lg p-4 ${isCancelled || isExpired ? 'bg-red-50' : 'bg-gray-50'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-semibold text-gray-900">
                      {currentPlan?.name || 'Free Tier'}
                    </p>
                    {(isCancelled || isExpired) && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {isExpired ? 'Expired' : 'Cancelled'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {isCancelled ? (
                      <>Cancellation pending - Premium access until {formatDate(subscription?.current_period_end)}</>
                    ) : isExpired ? (
                      'Subscription period has ended'
                    ) : isFreeTier ? (
                      'Free forever with basic features'
                    ) : (
                      `${subscription?.billing_cycle === 'annual' ? 'Annual' : 'Monthly'} billing`
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">
                    {isFreeTier ? (
                      '$0'
                    ) : (
                      formatCurrency(
                        subscription?.billing_cycle === 'annual' 
                          ? currentPlan?.annual_price 
                          : currentPlan?.monthly_price
                      )
                    )}
                  </p>
                  <p className="text-sm text-gray-600">
                    {!isFreeTier && `per month${subscription?.billing_cycle === 'annual' ? ', billed annually' : ''}`}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Details */}
          {!isFreeTier && subscription && !isCancelled && !isExpired && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Subscription Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{isPendingCancellation ? 'Access Until' : 'Next Billing Date'}</p>
                      <p className="text-sm text-gray-600">
                        {formatDate(subscription.current_period_end)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <DollarSign className="h-5 w-5 text-gray-400 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Status</p>
                      <p className="text-sm text-gray-600 capitalize">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isPendingCancellation
                            ? 'bg-yellow-100 text-yellow-800'
                            : subscription.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : subscription.status === 'past_due'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {isPendingCancellation ? 'Pending Cancellation' : subscription.status}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Cancelled Subscription Details */}
          {(isCancelled || isExpired) && subscription && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Cancellation Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {isExpired ? 'Expired On' : 'Access Until'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {formatDate(subscription.current_period_end) === 'N/A' ? (
                          <span className="italic text-gray-500">
                            End of current billing period
                          </span>
                        ) : (
                          formatDate(subscription.current_period_end)
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <DollarSign className="h-5 w-5 text-gray-400 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Status</p>
                      <p className="text-sm text-gray-600 capitalize">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {isExpired ? 'Expired' : 'Cancelled'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {formatDate(subscription.current_period_end) === 'N/A' && !isExpired && (
                <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-700">
                    <strong>Note:</strong> The exact cancellation date couldn't be determined. Your access will end at the end of your current billing period. Use the "Refresh" button to update with the latest information from your payment processor.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Plan Features */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Plan Features</h4>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(isCancelled || isExpired) ? (
                  <div className="col-span-full">
                    <p className="text-sm text-gray-600">
                      {isExpired ? (
                        'Subscription has expired. Reverting to Free Tier features.'
                      ) : (
                        'Subscription cancelled. You retain current plan features until the access period ends, then revert to Free Tier.'
                      )}
                    </p>
                  </div>
                ) : (
                  <>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Job Posts</p>
                      <p className="text-sm text-gray-600">
                        {currentPlan?.features?.active_job_posts || 0} active jobs
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Monthly Hires</p>
                      <p className="text-sm text-gray-600">
                        {currentPlan?.features?.hires_per_month === -1 
                          ? 'Unlimited' 
                          : `${currentPlan?.features?.hires_per_month || 0} hires`}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Candidate Hires</p>
                      <p className="text-sm text-gray-600">
                        {currentPlan?.features?.max_hires === -1 
                          ? 'Unlimited' 
                          : `Up to ${currentPlan?.features?.max_hires || 0} VAs`}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">AI Recommendations</p>
                      <p className="text-sm text-gray-600">
                        {currentPlan?.features?.ai_recommendations ? 'Included' : 'Not included'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* AI Credits Section */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
              <Zap className="h-4 w-4 mr-2 text-blue-600" />
              AI Generation Credits
            </h4>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
              {creditsLoading ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-6 bg-blue-200 rounded w-1/3"></div>
                  <div className="h-4 bg-blue-200 rounded w-2/3"></div>
                </div>
              ) : aiCredits ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Credits Remaining This Month</p>
                      <p className="text-xs text-gray-600 mt-1">
                        {userProfile?.subscription_tier ? `${userProfile.subscription_tier.charAt(0).toUpperCase() + userProfile.subscription_tier.slice(1)} Tier` : 'Free Tier'}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-blue-600">
                        {aiCredits.creditsRemaining >= 999999 ? '∞' : aiCredits.creditsRemaining}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {aiCredits.creditsRemaining >= 999999 ? 'Unlimited' : aiCredits.creditsRemaining === 1 ? 'credit' : 'credits'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded p-2 mt-2">
                    <p className="text-xs text-gray-700">
                      <strong>💡 How it works:</strong> Each AI job posting generation uses 1 credit. 
                      {aiCredits.creditsRemaining < 999999 && ' Credits reset on the 1st of each month.'}
                      {aiCredits.creditsRemaining >= 999999 && ' You have unlimited AI generations with your current plan!'}
                    </p>
                  </div>

                  {aiCredits.creditsRemaining === 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                      <p className="text-xs text-yellow-800">
                        <strong>⚠️ Out of credits:</strong> Upgrade your plan to get more AI generation credits.
                      </p>
                    </div>
                  )}

                  {aiCredits.creditsRemaining > 0 && aiCredits.creditsRemaining <= 2 && aiCredits.creditsRemaining < 999999 && (
                    <div className="bg-orange-50 border border-orange-200 rounded p-2">
                      <p className="text-xs text-orange-800">
                        <strong>📊 Low on credits:</strong> You have {aiCredits.creditsRemaining} credit{aiCredits.creditsRemaining === 1 ? '' : 's'} left.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-600">Unable to load credit information</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            {isCancelled || isExpired ? (
              <>
                <a
                  href="/pricing"
                  className="flex items-center justify-center px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                  style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
                >
                  Resubscribe
                </a>
                <a
                  href="/pricing"
                  className="flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  View All Plans
                </a>
              </>
            ) : !isFreeTier ? (
              <>
                <button
                  onClick={handleManageSubscription}
                  disabled={actionLoading}
                  className="flex items-center justify-center px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                  style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
                >
                  {actionLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <ExternalLink className="h-4 w-4 mr-2" />
                  )}
                  Manage Subscription
                </button>

                <button
                  onClick={() => setShowCancelConfirm(true)}
                  disabled={actionLoading || cancelConfirmLoading || isPendingCancellation}
                  title={isPendingCancellation ? 'Cancellation already scheduled for end of billing period' : undefined}
                  className="flex items-center justify-center px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <X className="h-4 w-4 mr-2" />
                  {isPendingCancellation ? 'Cancellation Pending' : 'Cancel Subscription'}
                </button>
              </>
            ) : (
              <a
                href="/pricing"
                className="flex items-center justify-center px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
              >
                Upgrade Plan
              </a>
            )}
            
            {!isCancelled && !isExpired && (
              <a
                href="/pricing"
                className="flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                View All Plans
              </a>
            )}
          </div>

          {!isFreeTier && !isCancelled && !isExpired && (
            <div className="text-xs text-gray-500 bg-blue-50 rounded-lg p-3">
              <p>
                <strong>Note:</strong> Use "Manage Subscription" to update your payment method, 
                change billing frequency, or cancel your subscription. Changes will be reflected 
                immediately in your account.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Cancellation Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h4 className="text-lg font-medium text-gray-900">Subscription Canceled</h4>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-6">
              <div className="flex items-center justify-center mb-4">
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <p className="text-center text-base font-medium text-gray-900 mb-2">
                Your subscription has been canceled.
              </p>
              {cancelSuccessDate ? (
                <p className="text-center text-sm text-gray-600">
                  You will retain access to premium features until{' '}
                  <span className="font-semibold text-gray-800">{cancelSuccessDate}</span>.
                  After that, your account will be moved to the Free Tier.
                </p>
              ) : (
                <p className="text-center text-sm text-gray-600">
                  You will retain access until the end of your current billing period, then be moved to the Free Tier.
                </p>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-center">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h4 className="text-lg font-medium text-gray-900">Cancel Subscription</h4>
              <button
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelConfirmLoading}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-4">
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Are you sure?</p>
                  <p className="text-sm text-red-700 mt-1">
                    Canceling your subscription will:
                  </p>
                  <ul className="text-sm text-red-700 list-disc list-inside mt-2 space-y-1">
                    <li>End your {currentPlan?.name} plan</li>
                    <li>Revert you to the free tier on your next billing cycle</li>
                    <li>Reduce your feature access</li>
                    <li>No refunds for partial months</li>
                  </ul>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                You can resubscribe anytime and will keep your account and history.
              </p>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelConfirmLoading}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancelConfirmLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {cancelConfirmLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Canceling...
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4 mr-2" />
                    Cancel Subscription
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;